import { Controller } from "@hotwired/stimulus"
import { trackWebEvent } from "../src/web_analytics"

export default class extends Controller {
    static values = {
        visits: { type: Number, default: 0 }
    }

    // Paths that should have special modal behavior
    static SPECIAL_PATHS = [
        '/analysis',
        '/ingredient-analyzer',
        '/compare',
        '/dupes',
        '/fungal-acne-checker',
        '/about',
        '/support',
        '/terms-of-service',
        '/privacy-policy',
        '/affiliate-disclosure',
        '/alternative/skincarisma'
    ]

    connect() {
        // Don't show modal on native app
        if (document.documentElement.hasAttribute('data-turbo-native-app')) return

        this.initializeVisitTracking()
        this.modalShown = false
        this.appDrawerShown = false
        this.lastScrollY = window.scrollY

        // Handle first visit with 30 second delay
        if (this.visits <= 1) {
            this.signUpTimeout = setTimeout(() => {
                this.showSignUpModal()
            }, 30000)
        } else {
            // Setup scroll listener for subsequent visits
            this.handleScroll = this.handleScroll.bind(this)
            window.addEventListener('scroll', this.handleScroll)
        }

        // The drawers are Stimulus controllers of their own and may connect
        // after this one, so retry the app drawer on the next turbo:load too.
        this.handleTurboLoad = this.handleTurboLoad.bind(this)
        document.addEventListener('turbo:load', this.handleTurboLoad)
        this.showAppDrawer()
    }

    showAppDrawer() {
        if (this.appDrawerShown) return

        // Show on the first pageview, at most once a day. The QA override
        // bypasses the cookie without setting it or recording an impression.
        const forceAppDrawer = new URLSearchParams(window.location.search).has('show_app_drawer')
        if (!forceAppDrawer && Cookies.get('app_drawer_shown')) return

        const appDrawer = document.querySelector(`[data-trigger-id*='app-drawer-modal-button']`)
        if (!appDrawer) return

        appDrawer.dispatchEvent(new Event('bottom-drawer:open'))
        if (!appDrawer.open) return

        this.appDrawerShown = true

        if (!forceAppDrawer) {
            Cookies.set('app_drawer_shown', 'true', { expires: 1 })
            trackWebEvent("app_drawer_shown", {
                path: window.location.pathname,
                visits: this.visits
            })
        }
    }

    initializeVisitTracking() {
        this.visits = parseInt(Cookies.get('visits') || 0)

        // Track initial pageview if this is the first controller connection
        if (!window.modalPresentationInitialized) {
            this.trackPageview()
            window.modalPresentationInitialized = true
        }
    }

    handleTurboLoad() {
        this.showAppDrawer()

        // Track pageview on Turbo navigation
        this.trackPageview()
    }

    trackPageview() {
        // Increment visits
        this.visits = this.visits + 1
        Cookies.set('visits', this.visits.toString())
    }

    disconnect() {
        clearTimeout(this.signUpTimeout)
        window.removeEventListener('scroll', this.handleScroll)
        document.removeEventListener('turbo:load', this.handleTurboLoad)
    }

    handleScroll() {
        // Don't show modal on scroll for the root path
        if (window.location.pathname === '/') return

        const currentScrollY = window.scrollY
        const isScrollingDown = currentScrollY > this.lastScrollY
        const isMobile = window.innerWidth < 768
        const scrollThreshold = isMobile ? window.innerHeight * 2 : window.innerHeight

        if (isScrollingDown && currentScrollY > scrollThreshold) {
            // On non-special pages, re-open the wall on each downward pass even
            // after a dismiss. This repeat exposure is the primary web-to-iOS
            // app conversion path. PR #3703's "once per pass" rewrite removed it
            // and coincided with a drop in native signups, so we restore the
            // original behavior. Special paths still show only once.
            if (!this.isSpecialPath() || !this.modalShown) {
                this.modalShown = false
                this.showSignUpModal()
            }
        }

        this.lastScrollY = currentScrollY
    }

    showSignUpModal() {
        if (this.modalShown) return

        // The app drawer opens on the first pageview, which is the same pageview
        // that arms the 30 second wall timer. Stacking the wall on an open
        // drawer buries both, so skip this pass and leave the drawer to finish.
        // Scoped to the app drawer so the wall still behaves normally alongside
        // every other drawer.
        const appDrawer = document.querySelector(`[data-trigger-id*='app-drawer-modal-button']`)
        if (appDrawer?.open) return

        const signUpDrawer = document.querySelector(`[data-trigger-id*='sign-up-modal-button']`)
        if (signUpDrawer) {
            signUpDrawer.dispatchEvent(new Event('bottom-drawer:open'))
            this.modalShown = true

            trackWebEvent("signup_wall_shown", {
                path: window.location.pathname,
                visits: this.visits,
                trigger: this.visits <= 1 ? "timer" : "scroll"
            })
        }
    }

    handleDismissSignUpModal() {
        this.modalShown = true

        trackWebEvent("signup_wall_dismissed", {
            path: window.location.pathname,
            visits: this.visits
        })

        // If not first visit and not a special path, scroll back to top
        if (this.visits > 1 && !this.isSpecialPath()) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
        }
    }

    isSpecialPath() {
        const currentPath = window.location.pathname

        if (currentPath === '/') return true // homepage
        if (currentPath.startsWith('/analysis')) return true // special case for analysis, we want to check if it starts with /analysis (not exact match of full path)

        return this.constructor.SPECIAL_PATHS.some(path =>
            currentPath === path ||
            (path === '/' && currentPath === '')
        )
    }
}
